import React, { useMemo } from "react";
import KioskFilters from "./KioskFilters";
import KioskDfaStatusCard from "./KioskDfaStatusCard";
import KioskMetricCard from "./KioskMetricCard";
import KioskMonitoringTable from "./KioskMonitoringTable";
import { formatKioskMetric } from "../hooks/kioskDashboard.helpers";

const Monitoring = ({ data, filters, onFilterChange, onReport, loading, error, onReload }) => {
  const summary = data && data.resumen ? data.resumen : {};
  const averages = summary.promedios || {};
  const rows = data && Array.isArray(data.usuarios) ? data.usuarios : [];
  const options = data && data.opciones ? data.opciones : {};
  const page = data && data.page ? data.page : 1;
  const pageSize = data && data.pageSize ? data.pageSize : 25;
  const total = data && data.total !== undefined ? data.total : rows.length;

  const periodLabel = useMemo(() => {
    const period = data && data.periodo;
    if (!period || (!period.fechaDesde && !period.fechaHasta)) return "Periodo actual";
    return `${period.fechaDesde || "…"} — ${period.fechaHasta || "…"}`;
  }, [data]);

  return (
    <div className="space-y-4">
      <KioskFilters
        filters={filters}
        onChange={onFilterChange}
        userOptions={options.usuarios || []}
        measurementOptions={options.mediciones || []}
      />

      {error && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
          <span>No se pudo cargar el monitoreo del kiosko.</span>
          <button type="button" onClick={onReload} className="font-bold underline">Reintentar</button>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 px-1">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 font-display">Resumen de monitoreo</p>
          <p className="text-sm font-medium text-slate-500 mt-1">{periodLabel}</p>
        </div>
        {loading && <span className="text-xs font-semibold text-[#0075AC]">Actualizando…</span>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KioskMetricCard label="Usuarios medidos" value={loading ? "…" : (summary.usuariosMedidos || 0)} icon="users" />
        <KioskMetricCard label="Sesiones completadas" value={loading ? "…" : (summary.sesionesCompletadas || 0)} icon="completed" accent="green" />
        <KioskMetricCard
          label="Tasa de completitud"
          value={loading ? "…" : summary.tasaCompletitud === null || summary.tasaCompletitud === undefined ? "N/D" : `${(summary.tasaCompletitud * 100).toFixed(0)}%`}
          icon="activity"
          accent="amber"
        />
        <KioskMetricCard label="Mediciones programadas" value={loading ? "…" : (summary.sesionesProgramadas || 0)} icon="calendar" />
      </div>

      <section className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm" aria-labelledby="kiosk-clinical-title">
        <div className="mb-4">
          <h2 id="kiosk-clinical-title" className="text-lg font-bold text-[#1D1F3F] font-display">Métricas clínicas del equipo</h2>
          <p className="text-xs text-slate-500 mt-1">Promedios de mediciones válidas; los datos ausentes se muestran como N/D</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KioskMetricCard label="HRV (RMSSD) promedio" value={loading ? "…" : formatKioskMetric(averages.rmssd)} icon="activity" />
          <KioskMetricCard label="SDNN promedio" value={loading ? "…" : formatKioskMetric(averages.sdnn)} icon="activity" />
          <KioskMetricCard label="pNN50 promedio" value={loading ? "…" : formatKioskMetric(averages.pnn50)} icon="activity" />
          <KioskMetricCard label="Puntaje cardiovascular" value={loading ? "…" : formatKioskMetric(averages.cardiovascularScore)} icon="completed" accent="green" />
          <KioskMetricCard label="DFA Alpha-1 promedio" value={loading ? "…" : formatKioskMetric(averages.alpha1)} icon="activity" accent="amber" />
          <KioskDfaStatusCard alpha1={averages.alpha1} loading={loading} />
        </div>
      </section>

      <KioskMonitoringTable
        rows={rows}
        onReport={onReport}
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={nextPage => onFilterChange("page", nextPage)}
      />
    </div>
  );
};

export default Monitoring;
